import { useEffect, useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { api, formatApiError } from "../../api/client";
import { useAuth } from "../../store/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { toast } from "sonner";

const ROLE_OPTIONS = [
  { value: "admin", label: "Administrador" },
  { value: "compliance_manager", label: "Responsable de cumplimiento" },
  { value: "auditor", label: "Auditor" },
  { value: "member", label: "Miembro" },
];

const ALL_ROLE_OPTIONS = [{ value: "owner", label: "Propietario" }, ...ROLE_OPTIONS];

const ROLE_LABELS = Object.fromEntries(ALL_ROLE_OPTIONS.map((r) => [r.value, r.label]));

const PAGE_SIZE = 25;

function fmt(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("es-ES");
  } catch {
    return d;
  }
}

export default function SettingsUsers() {
  const { user } = useAuth();
  const canManage = user?.role === "owner" || user?.role === "admin";
  const isOwner = user?.role === "owner";

  const [page, setPage] = useState(1);
  const [usersData, setUsersData] = useState({ items: [], total: 0 });
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const [u, i] = await Promise.all([
        api.get(`/users?page=${page}&page_size=${PAGE_SIZE}`),
        api.get("/invitations"),
      ]);
      setUsersData(u.data);
      setInvitations(i.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const totalPages = Math.max(1, Math.ceil(usersData.total / PAGE_SIZE));

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteError("");
    setInviting(true);
    try {
      const { data } = await api.post("/invitations", { email: inviteEmail, role: inviteRole });
      toast.success("Invitación enviada (mock). Revisa los logs del backend para el enlace.");
      if (data.accept_link) {
        toast.message("Enlace de invitación", { description: data.accept_link });
      }
      setInviteOpen(false);
      setInviteEmail("");
      setInviteRole("member");
      reload();
    } catch (err) {
      setInviteError(formatApiError(err.response?.data?.detail) || "No se pudo invitar.");
    } finally {
      setInviting(false);
    }
  };

  const cancelInvite = async (id) => {
    try {
      await api.delete(`/invitations/${id}`);
      toast.success("Invitación cancelada.");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const changeRole = async (uid, role) => {
    try {
      await api.put(`/users/${uid}/role`, { role });
      toast.success("Rol actualizado.");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const removeUser = async (uid) => {
    if (!window.confirm("¿Eliminar este usuario? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/users/${uid}`);
      toast.success("Usuario eliminado.");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="settings-users-page" className="max-w-5xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[20px] font-semibold tracking-tight text-slate-900">Usuarios</h1>
          <p className="text-[14px] text-slate-500 mt-1">
            Gestiona los miembros de tu organización.
          </p>
        </div>
        {canManage && (
          <Button onClick={() => setInviteOpen(true)} data-testid="btn-invite-user">
            <Plus className="h-4 w-4 mr-1" /> Invitar usuario
          </Button>
        )}
      </div>

      {invitations.length > 0 && (
        <div className="mt-6 bg-white border border-slate-200 rounded-md p-4">
          <h2 className="text-[14px] font-semibold text-slate-900 mb-3">
            Invitaciones pendientes ({invitations.length})
          </h2>
          <div className="space-y-2">
            {invitations.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between text-[14px] py-1" data-testid={`invitation-row-${inv.email}`}>
                <div>
                  <span className="text-slate-900">{inv.email}</span>
                  <span className="text-slate-400 ml-2">·</span>
                  <span className="text-slate-500 ml-2">{ROLE_LABELS[inv.role]}</span>
                  <span className="text-slate-400 text-[12px] ml-2">
                    Invitada el {fmt(inv.created_at)}
                  </span>
                </div>
                {canManage && (
                  <button
                    onClick={() => cancelInvite(inv.id)}
                    data-testid={`cancel-invite-${inv.email}`}
                    className="text-slate-400 hover:text-destructive p-1"
                    title="Cancelar invitación"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 bg-white border border-slate-200 rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>Correo</TableHead>
              <TableHead>Rol</TableHead>
              <TableHead>Último acceso</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-slate-500 py-8">Cargando...</TableCell>
              </TableRow>
            )}
            {!loading && usersData.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-slate-500 py-8">Sin usuarios.</TableCell>
              </TableRow>
            )}
            {!loading && usersData.items.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                <TableCell className="font-medium text-slate-900">{u.name}</TableCell>
                <TableCell className="text-slate-600">{u.email}</TableCell>
                <TableCell>
                  {isOwner && u.role !== "owner" && u.id !== user.id ? (
                    <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                      <SelectTrigger className="h-8 w-44" data-testid={`role-select-${u.email}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLE_OPTIONS.map((r) => (
                          <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="text-slate-700">{ROLE_LABELS[u.role]}</span>
                  )}
                </TableCell>
                <TableCell className="text-slate-500 text-[12px]">{fmt(u.last_login)}</TableCell>
                <TableCell className="text-right">
                  {canManage && u.role !== "owner" && u.id !== user.id && (
                    <button
                      onClick={() => removeUser(u.id)}
                      data-testid={`delete-user-${u.email}`}
                      className="text-slate-400 hover:text-destructive p-1"
                      title="Eliminar usuario"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between mt-4 text-[12px] text-slate-500">
        <span>
          Total: {usersData.total} · Página {page} de {totalPages}
        </span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            data-testid="users-prev-page"
          >
            Anterior
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            data-testid="users-next-page"
          >
            Siguiente
          </Button>
        </div>
      </div>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent data-testid="invite-modal">
          <DialogHeader>
            <DialogTitle>Invitar usuario</DialogTitle>
            <DialogDescription>
              El usuario recibirá un enlace para crear su cuenta y unirse a la organización.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4">
            <div>
              <Label htmlFor="inv-email">Correo electrónico</Label>
              <Input
                id="inv-email"
                type="email"
                required
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                data-testid="invite-email"
              />
            </div>
            <div>
              <Label>Rol</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {inviteError && <div data-testid="invite-error" className="text-[12px] text-destructive">{inviteError}</div>}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setInviteOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={inviting} data-testid="invite-submit">
                {inviting ? "Enviando..." : "Enviar invitación"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
